import type { IconName } from '@customerio/pluma-icons';
import { emailChannel } from './channels/email';
import { inAppChannel } from './channels/in-app';
import { inboxChannel } from './channels/inbox';
import { lineChannel } from './channels/line';
import { liveNotificationChannel } from './channels/live-notification';
import { pushChannel } from './channels/push';
import { slackChannel } from './channels/slack';
import { smsChannel } from './channels/sms';
import { twilioChannel } from './channels/twilio';
import { urbanAirshipChannel } from './channels/urban-airship';
import { webhookChannel } from './channels/webhook';
import { whatsappChannel } from './channels/whatsapp';

/** Every messaging channel, keyed by id. Adding a channel is one file in `channels/` and one line here. */
export const CHANNEL_REGISTRY = {
  [emailChannel.id]: emailChannel,
  [webhookChannel.id]: webhookChannel,
  [twilioChannel.id]: twilioChannel,
  [smsChannel.id]: smsChannel,
  [urbanAirshipChannel.id]: urbanAirshipChannel,
  [slackChannel.id]: slackChannel,
  [pushChannel.id]: pushChannel,
  [inAppChannel.id]: inAppChannel,
  [inboxChannel.id]: inboxChannel,
  [lineChannel.id]: lineChannel,
  [whatsappChannel.id]: whatsappChannel,
  [liveNotificationChannel.id]: liveNotificationChannel,
} as const;

export type ChannelId = keyof typeof CHANNEL_REGISTRY;

/**
 * A deliberate subset of channels, for a surface that supports only some of
 * them: `ChannelSubset<'email' | 'sms' | 'whatsapp'>`. Every member must be a
 * registry id, so a typo or a removed channel fails to compile.
 */
export type ChannelSubset<T extends ChannelId> = T;

export const CHANNELS = Object.values(CHANNEL_REGISTRY);

const CHANNEL_IDS_BY_VALUE: ReadonlyMap<string, ChannelId> = new Map(
  CHANNELS.map((channel) => [channel.id, channel.id]),
);

/** The channel id a string names, or null for one that isn't a channel. */
export function toChannelId(value: string): ChannelId | null {
  return CHANNEL_IDS_BY_VALUE.get(value) ?? null;
}

/** The icon that stands for a channel. */
export function getChannelIcon(id: ChannelId): IconName {
  return CHANNEL_REGISTRY[id].icon;
}

/** The icon for a string that may name a channel, or `fallback` for one that doesn't. */
export function channelIconOrFallback(value: string, fallback: IconName): IconName {
  const id = toChannelId(value);
  return id === null ? fallback : getChannelIcon(id);
}
